/*
 * Toggle.js
 *
 * Accessible region toggle.
 * Toggles a region's visibility with a button by manipulating ARIA states.
 * Automatically hides other regions managed by buttons in a matched set of elements.
 *
 * @link https://github.com/croxton/toggle.js
 * @author Mark Croxton
 * @version 1.0.0
 * @copyright Mark Croxton 2020
 */

import PubSub from "pubsub-js";

(function(doc, proto) {
    try { // check if browser supports :scope natively
        doc.querySelector(':scope body');
    } catch (err) { // polyfill native methods if it doesn't
        ['querySelector', 'querySelectorAll'].forEach(function(method) {
            var nativ = proto[method];
            proto[method] = function(selectors) {
                if (/(^|,)\s*:scope/.test(selectors)) { // only if selectors contains :scope
                    var id = this.id; // remember current element id
                    this.id = 'ID_' + Date.now(); // assign new unique id
                    selectors = selectors.replace(/((^|,)\s*):scope/g, '$1#' + this.id); // replace :scope with #ID
                    var result = doc[method](selectors);
                    this.id = id; // restore previous id
                    return result;
                } else {
                    return nativ.call(this, selectors); // use native code for other selectors
                }
            }
        });
    }
})(window.document, Element.prototype);

class Toggle {

    /*
     * Setup buttons and panels
     */
    constructor(el, init= false, linked = true, closeChildren = false) {

        let self = this;
        this.buttons = document.querySelectorAll(el);
        this.init = init;
        this.linked = linked;
        this.closeChildren = closeChildren;
        this.delayFocus = null;
        this.inMotion = false;
        this.activeRootTimeout = null;

        // set initial toggle state (responsive)
        if (this.init) {
            self.resetToggleState();
        }

        // hover container
        let hoverContainer = null;
        let inMotionTimeout = null;

        for (let i=0; i < this.buttons.length; i++) {

            this.buttons[i].classList.add('is-toggle-btn');
            // hover - optionally open the panel when hovering over a button
            // when the supplied media query matches
            const canHover = !(matchMedia('(hover: none)').matches);

            if (this.buttons[i].dataset.hover && canHover) {
                let hoverElm = this.buttons[i];
                if (this.buttons[i].dataset.hoverTargetId) {
                    hoverElm = document.getElementById(this.buttons[i].dataset.hoverTargetId);
                }
                hoverElm.addEventListener("mouseenter", function(e) {
                    if (self.buttons[i].getAttribute('aria-expanded') === 'false') {
                        let mediaQueryHover = window.matchMedia(self.buttons[i].dataset.hover);
                        if (mediaQueryHover.matches) {
                            self.inMotion = false;
                            self.toggle(self.buttons[i]);
                            self.inMotion = true;
                            clearTimeout(inMotionTimeout);
                            inMotionTimeout = setTimeout(() => {
                                self.inMotion = false;
                            }, 300);
                        }
                    }

                    // update triangle position
                    let bounds = hoverElm.getBoundingClientRect();
                    hoverElm.style.setProperty('--mouse-x', e.clientX - bounds.left + "px");
                });
                this.buttons[i].addEventListener("click", (e) => {
                    e.preventDefault();
                    self.toggle(e.currentTarget);
                });

                // close all open child panels on mouseleave of closest parent element with attribute [data-hover-close]
                if (!hoverContainer) {
                    hoverContainer = this.buttons[i].closest('[data-hover-close]');
                    if (hoverContainer) {
                        hoverContainer.addEventListener("mouseleave", function (e) {
                            let mediaQueryHover = window.matchMedia(self.buttons[i].dataset.hover);
                            if (mediaQueryHover.matches) {
                               self.closeAll();
                            }
                        });
                    }
                }
            } else {
                this.buttons[i].addEventListener("click", (e) => {
                    e.preventDefault();
                    self.toggle(e.currentTarget);
                });
            }

            // if there is a 'close' button in the panel associated with this button,
            // allow it to close the panel (if it is open), and return focus to the button
            let btnPanel = document.querySelector('#' + this.buttons[i].getAttribute('aria-controls'));

            if (btnPanel != null) {
                setTimeout(() => {
                    btnPanel.classList.add('is-toggled');
                }, 300);

                let closeBtns = btnPanel.querySelectorAll(':scope > [data-close]');
                for (let [j, closeBtn] of [...closeBtns].entries()) {
                    if (closeBtn != null) {
                        if (closeBtn.tagName !== 'BUTTON') {
                            closeBtn = closeBtn.querySelector('button');
                        }
                        if (closeBtn) {
                            closeBtn.addEventListener("click", () => {
                                if (self.buttons[i].getAttribute('aria-expanded') === 'true') {
                                    self.buttons[i].click(); // triggers doToggle()
                                    self.buttons[i].focus();
                                }
                            });
                        }

                    }
                }

                // allow click on panel itself to close, or target id
                if (btnPanel.hasAttribute('data-close')) {
                    let target = document.getElementById(btnPanel.getAttribute('data-close')) ?? btnPanel;
                    if (target) {
                        target.addEventListener("click", function(e) {
                            if (e.target === this) {
                                if (self.buttons[i].getAttribute('aria-expanded') === 'true') {
                                    self.buttons[i].click(); // triggers doToggle()
                                    self.buttons[i].focus();
                                }
                            }
                        });
                    }
                }
            }
        }

        // allow user to press the ESCAPE key to close any open regions
        window.addEventListener("keyup", (e)=> {
            let key = e.key || e.keyCode;
            if (key === 'Escape' || key === 'Esc' || key === 27) {
                self.closeAll('last');
            }
        });

        // subscribe to window resize done event
        window.addEventListener('resizeDone', () => {
            self.resetToggleState();
        });
    }

    resetToggleState() {

        for (let i=0; i < this.buttons.length; i++) {

            let btn = this.buttons[i];
            let btnPanel = document.querySelector('#' + btn.getAttribute('aria-controls'));

            if (btnPanel != null && ! btn.hasAttribute('data-toggle-once')) {

                // remove aria state and calculate 'natural' display property
                btnPanel.setAttribute('aria-hidden', null);

                if (window.getComputedStyle(btnPanel).display === 'none' || window.getComputedStyle(btnPanel).visibility === 'hidden') {
                    this.closePanel(btn, btnPanel);
                } else {
                    this.openPanel(btn, btnPanel, false);
                }
            }

        }
    }

    /*
     * Event handler for region toggle
     */
    toggle(targetBtn, state, focus) {

        if (this.inMotion) {
            return;
        }

        state = state ?? (targetBtn.getAttribute('aria-expanded') === 'false');
        focus = focus ?? true;

        let targetPanel = document.querySelector('#' + targetBtn.getAttribute('aria-controls'));

        // update classes
        if (state === true) {

            // when opening a panel, close other open buttons in the matched set - if already open
            if (this.linked) {
                for (let i=0; i < this.buttons.length; i++) {
                    let btn = this.buttons[i];
                    if (btn !== targetBtn && btn.getAttribute('aria-expanded') === 'true') {
                        let btnPanel = document.querySelector('#' + btn.getAttribute('aria-controls'));
                        this.closePanel(btn, btnPanel);
                    }
                }
            }

            // now open the selected panel
            this.openPanel(targetBtn, targetPanel, focus);

        } else {
            // closing an open panel
            this.closePanel(targetBtn, targetPanel);

            // when closing a panel, close child panels
            if (this.closeChildren) {
                let children = targetPanel.querySelectorAll('.is-toggle-btn[aria-expanded="true"]');
                if (children) {
                    for (let i=0; i < children.length; i++) {
                        children[i].click();
                    }
                }
            }
        }
    }

    closePanel(btn, panel) {

        // update state
        btn.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');

        // reset tabIndexes
        let tabIndexes = panel.querySelectorAll('[tabindex]');
        for (let i=0; i < tabIndexes.length; i++) {
            tabIndexes[i].setAttribute("tabindex", "-1");
        }

        // self active class
        if (btn.dataset.active) {
            btn.classList.remove(btn.dataset.active);
        }

        // parent active class
        if (btn.dataset.activeParent) {
            btn.parentNode.classList.remove(btn.dataset.activeParent);
        }

        // root active class
        if (btn.dataset.activeRoot) {
            let root;
            if (btn.dataset.activeRootId) {
                root = document.getElementById(btn.dataset.activeRootId);
            } else {
                root = document.querySelector('html');
            }
            if (root) {
                root.classList.remove(btn.dataset.activeRoot);
            }
        }

        // target active class
        if (btn.dataset.activeTarget && btn.dataset.activeTargetId) {
            let target = document.getElementById(btn.dataset.activeTargetId);
            if (target) {
                target.classList.remove(btn.dataset.activeTarget);
            }
        }

        // Modal window? Un-fix background
        if (btn.dataset.modal && btn.dataset.modal === "true") {
            const body = document.body;
            const scrollY = body.style.top;
            if (body.classList.contains('has-toggle-fixed')) {
                body.style.position = '';
                body.style.top = '';
                body.classList.remove('has-toggle-fixed');
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }
        // notify subscribers
        PubSub.publish('toggleClose', btn);
    }

    openPanel(btn, panel, focus) {

        // opening a panel
        btn.setAttribute('aria-expanded', 'true');
        panel.setAttribute('aria-hidden', 'false');

        // toggle any tabIndexes in the region...
        let tabIndexes = panel.querySelectorAll('[tabindex]');
        for (let i=0; i < tabIndexes.length; i++) {
            tabIndexes[i].setAttribute("tabindex", "0");
        }

        // ..but tabindex for links in nested regions should remain inactive
        let tabIndexesNested = panel.querySelectorAll('[aria-hidden="true"] [tabindex="0"]');
        for (let i=0; i < tabIndexesNested.length; i++) {
            tabIndexesNested[i].setAttribute("tabindex", "-1");
        }

        // activate self
        if (btn.dataset.active) {
            btn.classList.add(btn.dataset.active);
        }

        // activate parent
        if (btn.dataset.activeParent) {
            btn.parentNode.classList.add(btn.dataset.activeParent);
        }

        // activate root
        if (btn.dataset.activeRoot) {
            let root;
            if (btn.dataset.activeRootId) {
                root = document.getElementById(btn.dataset.activeRootId);
            } else {
                root = document.querySelector('html');
            }
            if (root) {
                root.classList.add(btn.dataset.activeRoot);
            }
        }

        // target active class
        if (btn.dataset.activeTarget && btn.dataset.activeTargetId) {
            let target = document.getElementById(btn.dataset.activeTargetId);
            if (target) {
                document.getElementById(btn.dataset.activeTargetId).classList.add(btn.dataset.activeTarget);
            }
        }

        // Modal window? Fix background and prevent scrolling
        if (btn.dataset.modal && btn.dataset.modal === "true") {
            const scrollY = window.scrollY;
            const body = document.body;
            if (body.classList.contains('is-mobile')) {
                body.classList.add('has-toggle-fixed');
                body.style.position = 'fixed';
                body.style.top = `-${scrollY}px`;
                body.style.width = `100%`;
            }
        }

        // focus first element in the newly opened panel
        if (focus) {
            if (btn.dataset.focus) {
                let focusable = panel.querySelector(btn.dataset.focus);
                if (focusable) {
                    setTimeout(() => {
                        focusable.focus();
                    }, 300);
                }

            } else {
                let focusable = panel.querySelectorAll('button:not([data-close]), [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
                if (focusable.length) {
                    let firstFocusable = focusable[0];
                    setTimeout(() => {
                        firstFocusable.focus();
                    }, 300);
                }
            }

        }

        // notify subscribers
        PubSub.publish('toggleOpen', btn);
    }

    closeAll(focusElement = '') {
        this.inMotion = false;
        for (let i=0; i < this.buttons.length; i++) {
            let btn = this.buttons[i];
            if (btn.getAttribute('aria-expanded') === 'true') {
                this.toggle(btn);
                if (focusElement !== '') {
                    if (focusElement === 'last' && i === (this.buttons.length - 1)) {
                        // return focus to last button in sequence
                        btn.focus();
                    } else if (focusElement === 'first' && i === 0) {
                        // return focus to first button in sequence
                        btn.focus();
                    }
                }
            }
        }
    }
}

export default Toggle;